import SEO from "@/components/SEO";
import { PageHero, Section, SectionHead, FAQ } from "@/components/sections";
import { Reveal } from "@/components/Reveal";
import { CTALink } from "@/components/ui-kit";
import LeadForm from "@/components/LeadForm";
import { Link } from "react-router-dom";
import { LineChart, Layers, Landmark, HeartPulse, Car, Activity } from "lucide-react";

const services = [
  { icon: LineChart, name: "Mutual Funds", to: "/products/mf-pms-aif", desc: "Goal-based portfolios across equity, hybrid and debt via SIP or lumpsum — built to a plan, not a hot tip." },
  { icon: Layers, name: "PMS & AIF", to: "/products/mf-pms-aif", desc: "Discretionary portfolios and alternatives for eligible investors (PMS ₹50L+, AIF ₹1Cr+ per SEBI)." },
  { icon: Activity, name: "Direct Equity & Trading", to: "/products/direct-equity", desc: "Direct equity execution and research access, in partnership with Sharekhan." },
  { icon: Landmark, name: "Bonds & Fixed Income", to: "/products/fixed-income-insurance", desc: "Corporate bonds and fixed-income options to add stability and income to your portfolio." },
  { icon: HeartPulse, name: "Health & Personal Accident", to: "/products/fixed-income-insurance", desc: "Protection so a medical event doesn't undo years of saving. Insurance is the subject matter of solicitation." },
  { icon: Car, name: "Motor Insurance", to: "/products/fixed-income-insurance", desc: "Comprehensive motor cover arranged as part of your overall protection plan." },
];

const faq = [
  { q: "How much do I need to start?", a: "You can begin a SIP with a modest monthly amount. PMS and AIF have SEBI-mandated minimums (₹50 lakh and ₹1 crore respectively)." },
  { q: "Are direct or regular plans used?", a: "We explain the difference transparently and recommend what fits your need for advice, service and cost." },
  { q: "Will you keep reviewing my portfolio?", a: "Yes. We schedule structured reviews rather than selling once and disappearing. Markets and life both change." },
];

export default function Individual() {
  return (
    <div>
      <SEO path="/individual" title="Wealth Solutions for Individuals" description="Mutual funds, PMS, AIF, direct equity, bonds and insurance — planned around your goals by Rao Investment Solutions, an AMFI-registered distributor." faq={faq} />
      <PageHero
        label="For Individuals"
        title="Build wealth to a plan — and protect it while you do."
        intro="From your first SIP to retirement and estate goals, one advisor sees the whole picture: growth, income and protection."
        breadcrumbs={[{ name: "Home", to: "/" }, { name: "Individual" }]}
      />

      <Section>
        <div className="grid md:grid-cols-3 gap-6">
          {services.map((s, i) => (
            <Reveal key={i} delay={i * 0.05}>
              <Link to={s.to} className="block h-full bg-white p-8 border border-navy/10 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 group">
                <s.icon className="w-7 h-7 text-orange" strokeWidth={1.25} />
                <h3 className="mt-5 font-serif text-xl text-navy group-hover:text-orange transition-colors">{s.name}</h3>
                <p className="mt-3 font-sans text-sm text-slate leading-relaxed">{s.desc}</p>
              </Link>
            </Reveal>
          ))}
        </div>
      </Section>

      <Section cream>
        <SectionHead label="Start somewhere" title="Not sure where to begin?" intro="Pick the path that sounds like you. Each includes an illustrative calculator and a simple next step." />
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { name: "Start Investing", to: "/start-investing" },
            { name: "Build Wealth Through SIP", to: "/sip-investment" },
            { name: "Plan for Retirement", to: "/retirement-planning" },
          ].map((r, i) => (
            <Reveal key={i} delay={i * 0.06}>
              <Link to={r.to} className="block bg-navy text-white p-8 hover:bg-navy-deep transition-colors group">
                <h3 className="font-serif text-2xl">{r.name}</h3>
                <span className="mt-4 inline-block font-sans text-sm text-orange group-hover:translate-x-1 transition-transform">Explore →</span>
              </Link>
            </Reveal>
          ))}
        </div>
      </Section>

      <Section>
        <div className="max-w-3xl"><SectionHead label="FAQ" title="Questions from investors" /><FAQ items={faq} /></div>
      </Section>

      <Section dark>
        <div className="grid lg:grid-cols-2 gap-14 items-start">
          <SectionHead label="Next step" title="Let's turn goals into a plan." dark intro="Share where you are and we'll suggest a first, sensible step — no jargon, no pressure." />
          <div className="bg-white/5 border border-white/10 p-8 md:p-10">
            <LeadForm dark audience="individual" title="Request a wealth consultation" />
          </div>
        </div>
      </Section>
    </div>
  );
}
